import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  message,
  Row,
  Select,
  Upload,
  UploadProps,
} from 'antd';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getAllUserProfileBatch, inserExportUserProfileBatch } from 'src/api/user_profile';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import { usePermission } from 'src/hooks/usePermission';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { setUserProfileState } from 'src/redux/slice/userProfile';
import { ProfileBatchStatus } from 'src/shared/common/userProfile';
import { UserProfileBatchResponse } from 'src/shared/interface/user_profile';
import { exportExcel, formatDateTime, parseQueryString } from 'src/utils';

function UsersProfileBatchList() {
  const size = useWindowSize();
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = location.search;
  const { openTabs, activeKeyId } = useAppSelector((state) => state.global);
  const { isRefresh } = useAppSelector((state) => state.userProfile);
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');

  const [data, setData] = useState<PaginationResponse<UserProfileBatchResponse[]>>();
  const [sortColumn, setSortColumn] = useState<ISortColumn>();
  const [file, setFile] = useState<any>();

  const { hasPermission, PERMISSIONS } = usePermission();

  useEffect(() => {
    fetchListUserProfileBatch();
    setSortColumn(undefined);
    form.resetFields();
  }, [isRefresh, activeKeyId]);

  const fetchListUserProfileBatch = async (params = {}) => {
    const res = await getAllUserProfileBatch(params);
    if (res) {
      setData(res);
    }
  };
  function handleExport() {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `danh_sach_HSNSD_theo_lo_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    const paramsExport = params
      ? params + `&Type=2&EnterpriseId=${JSON.parse(merchantInfo as string)?.merchantId ?? ''}`
      : `?Type=2&EnterpriseId=${JSON.parse(merchantInfo as string)?.merchantId ?? ''}`;

    exportExcel(
      process.env.REACT_APP_API_URL +
        '/api/user-profile/overflow-user-profile/export' +
        paramsExport,
    )
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  }

  function handleFormSearch(value: any) {
    let values = { ...value };
    values = {
      ...values,
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : undefined,
      dateRange: undefined,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    setSortColumn(undefined);
    setSearchParams({
      pageIndex: 1,
      pageSize: 10,
      ...values,
    });
    fetchListUserProfileBatch({
      ...values,
      pageIndex: 1,
      pageSize: 10,
    });
  }

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Số lô',
      fieldName: 'lotId',
      renderSpecialCard: (userBatch: UserProfileBatchResponse) => (
        <button
          disabled={!hasPermission(PERMISSIONS.USER_PROFILE.BATCH_VIEW_DETAIL)}
          className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
            !hasPermission(PERMISSIONS.USER_PROFILE.BATCH_VIEW_DETAIL)
              ? 'text-gray-400'
              : 'text-blue-400'
          } font-semibold`}
          onClick={() => openDetailUserProfileBatchPage(userBatch.lotId)}
        >
          {userBatch.lotId ?? ''}
        </button>
      ),
    },
    {
      name: 'Tên file',
      fieldName: 'fileName',
      isHideSort: true,
    },
    {
      name: 'Trạng thái',
      fieldName: 'status',
      isHideSort: true,
    },
    {
      name: 'Tổng cộng',
      fieldName: 'total',
    },
    {
      name: 'Imported thành công',
      fieldName: 'importSuccess',
      // renderSpecialCard: (record: UserProfileBatchResponse) => {
      //   return <span>{record.toTalAmount ? formatMoney(record.toTalAmount, 'đ') : 0}</span>;
      // },
    },
    {
      name: 'Imported thất bại',
      fieldName: 'importFailed',
    },
    {
      name: 'Chờ duyệt',
      fieldName: 'waiting',
      width: '5%',
    },
    {
      name: 'Đã duyệt',
      fieldName: 'approved',
      width: '5%',
    },
    {
      name: 'Từ chối',
      fieldName: 'reject',
      width: '5%',
    },
    {
      name: 'Ngày import',
      fieldName: 'createdAt',
      renderSpecialCard: (record: UserProfileBatchResponse) => {
        return <span>{record.createdAt ? formatDateTime(record.createdAt) : ''}</span>;
      },
    },
    {
      name: 'Thao tác',
      fieldName: 'id',
      renderSpecialCard: (userBatch: UserProfileBatchResponse) => (
        <button
          disabled={!hasPermission(PERMISSIONS.USER_PROFILE.BATCH_VIEW_DETAIL)}
          className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
            !hasPermission(PERMISSIONS.USER_PROFILE.BATCH_VIEW_DETAIL)
              ? 'text-gray-400'
              : 'text-blue-400'
          } font-semibold`}
          onClick={() => openDetailUserProfileBatchPage(userBatch.lotId)}
        >
          Xem chi tiết
        </button>
      ),
      isHideSort: true,
    },
  ];

  function openDetailUserProfileBatchPage(id?: string) {
    if (id) {
      dispatch(
        setGlobalState({
          activeKeyMenu: `user-profile/batch-detail?lotId=${id}`,
        }),
      );
      if (!openTabs.find((tab) => tab.key === `user-profile/batch-detail?lotId=${id}`)) {
        dispatch(
          setGlobalState({
            openTabs: [
              ...openTabs,
              {
                label: 'Chi tiết tạo lô hồ sơ người sử dụng ' + id,
                children: '',
                key: `user-profile/batch-detail?lotId=${id}`,
                closable: true,
              },
            ],
          }),
        );
      }
      dispatch(setGlobalState({ order: undefined }));
      navigate(`/management/user-profile/batch-detail?lotId=${id}`);
    }
  }

  const propsUpload: UploadProps = {
    beforeUpload: (file) => {
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (!isExcel) {
        message.error(`Định dạng file không đúng`);
      }
      return isExcel || Upload.LIST_IGNORE;
    },
    onChange: ({ file }) => {
      if (file.status === 'removed') {
        setFile(null);
      } else {
        file.status = 'done';
        setFile(file);
      }
    },
    //eslint-disable-next-line
    customRequest: () => {},
  };

  const handleImportFile = async () => {
    if (file) {
      const res = await inserExportUserProfileBatch({
        excelFile: file.originFileObj ? file.originFileObj : file,
      });
      if (res) {
        dispatch(setUserProfileState({ isRefresh: !isRefresh }));
      }
    } else {
      message.error('Import không thành công. Vui lòng thử lại!');
    }
  };

  const handleExportTemplateImport = () => {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `mau_import_sim.xlsx`;
    const url = process.env.REACT_APP_API_URL + '/api/Provider/export-excel-template?type=4';
    exportExcel(url)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  };
  return (
    <Fragment>
      <Form onFinish={handleFormSearch} form={form} layout='inline'>
        {/* <Row className='gap-4'> */}
        <div className='flex flex-wrap justify-between gap-4 w-full'>
          <div className='flex gap-2'>
            {hasPermission(PERMISSIONS.USER_PROFILE.BATCH_IMPORT_EXCEL) && (
              <div className='space-y-4'>
                {file && (
                  <div className='mt-2 flex items-center gap-2'>
                    <span className='w-[300px] truncate text-blue-600'>{file.name}</span>
                    <Button
                      danger
                      size={'small'}
                      icon={<DeleteOutlined />}
                      onClick={() => setFile(null)}
                    />
                  </div>
                )}
                <Upload
                  {...propsUpload}
                  maxCount={1}
                  accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  showUploadList={false}
                  fileList={file ? [file] : []}
                >
                  <Button type='dashed'>Chọn File</Button>
                </Upload>

                <Button type='primary' onClick={handleImportFile} disabled={!file} className='ml-2'>
                  Import
                </Button>
              </div>
            )}
          </div>
          <div className='flex flex-wrap gap-2'>
            <Form.Item name='dateRange' label='Ngày tạo'>
              <DatePicker.RangePicker
                disabledDate={(current) => current && current >= moment().endOf('day')}
                placeholder={['từ', 'đến']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>
            <Form.Item name='status' label='Trạng thái'>
              <Select
                showSearch
                className='min-w-[200px]'
                defaultValue={'Tất cả'}
                placeholder='Vui lòng chọn'
                options={ProfileBatchStatus}
              ></Select>
            </Form.Item>
          </div>
          <div className='flex mb-12 gap-4 flex-wrap'>
            <Button type='primary' htmlType='submit'>
              Tìm kiếm
            </Button>
            {hasPermission(PERMISSIONS.USER_PROFILE.BATCH_EXPORT_TEMPLATE) && (
              <Button onClick={() => handleExportTemplateImport()}>Export template</Button>
            )}
            {hasPermission(PERMISSIONS.USER_PROFILE.BATCH_EXPORT) && (
              <Button onClick={handleExport}>Export</Button>
            )}
          </div>
        </div>
      </Form>
      {size.width < 1024 ? (
        <CGridView
          length={data?.results.length}
          totalRecord={data?.rowCount ?? 0}
          columns={columns}
          title={() => (
            <div className='text-xl font-bold my-3'>Danh sách hồ sơ người sử dụng theo lô</div>
          )}
        >
          {data?.results && data?.results.length > 0 ? (
            data?.results.map((item: UserProfileBatchResponse) => {
              return (
                <div key={'data-row-' + item.id}>
                  <Divider className='border-t-black' />

                  <Row className='mb-6 items-center' gutter={[4, 16]}>
                    {columns.map((ele) => {
                      if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                        return (
                          <>
                            <Col span={11} className={`font-bold label`}>
                              <div className='flex gap-2 items-center'>{ele.name}</div>
                            </Col>
                            <Col className='break-words text-right' span={13}>
                              {ele?.renderSpecialCard
                                ? ele?.renderSpecialCard(item, null, null, null)
                                : item[`${ele?.fieldName}`]}
                            </Col>
                          </>
                        );
                      } else if (ele.fieldName === 'action') {
                        return (
                          <Col key={'column-' + item.id + ele.fieldName} span={24}>
                            <div className='flex gap-6'>
                              {ele?.renderSpecialCard
                                ? ele?.renderSpecialCard(item, null, null, null)
                                : item[`${ele?.fieldName}`]}
                            </div>
                          </Col>
                        );
                      } else {
                        return <></>;
                      }
                    })}
                  </Row>
                </div>
              );
            })
          ) : (
            <p>{'Không có dữ liệu'}</p>
          )}
        </CGridView>
      ) : (
        <TableContent
          bordered
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Không tìm thấy kết quả' }}
          dataSource={data?.results ?? []}
          columns={columns}
          totalRecord={data?.rowCount ?? 0}
          sortColumn={sortColumn}
          onPageChange={(pageNumber, pageSize) => {
            const pagination = parseQueryString(location.search);
            fetchListUserProfileBatch({
              ...pagination,
              pageIndex: pageNumber ?? 1,
              pageSize: pageSize ?? 10,
              sortField: pagination.sortField ?? undefined,
              asc: pagination.asc ?? undefined,
            });
          }}
          onSort={(sort: ISortColumn) => {
            const pagination = parseQueryString(location.search);
            fetchListUserProfileBatch({
              ...pagination,
              pageIndex: pagination.pageIndex ?? 1,
              pageSize: pagination.pageSize ?? 10,
              sortField: sort.fieldName,
              asc:
                sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
            });
          }}
        />
      )}
    </Fragment>
  );
}

export default UsersProfileBatchList;
